import { AppWindow, CheckCircle, Clock, Layers, UsersRound, XCircle } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ForgeDashboardStats } from '../../types';
import { cn } from '../../lib/utils';

interface ForgeStatsCardProps {
  stats: ForgeDashboardStats;
  className?: string;
}

interface StatItemProps {
  label: string;
  value: number;
  icon: React.ElementType;
  iconBgColor?: string;
  iconColor?: string;
}

const StatItem: React.FC<StatItemProps> = ({
  label,
  value,
  icon: Icon,
  iconBgColor = 'bg-primary/10',
  iconColor = 'text-primary',
}) => (
  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
    <div className={cn('p-2 rounded-lg', iconBgColor)}>
      <Icon className={cn('h-5 w-5', iconColor)} />
    </div>
    <div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  </div>
);

interface StatusBreakdownProps {
  title: string;
  items: Array<{ label: string; count: number; color: string }>;
}

const StatusBreakdown: React.FC<StatusBreakdownProps> = ({ title, items }) => {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">{total} total</span>
      </div>
      
      {/* Stacked bar */}
      <div className="h-3 bg-muted rounded-full overflow-hidden flex">
        {items.map((item, index) => {
          const width = total > 0 ? (item.count / total) * 100 : 0;
          if (width === 0) return null;
          return (
            <div
              key={index}
              className={cn('h-full', item.color)}
              style={{ width: `${width}%` }}
              title={`${item.label}: ${item.count}`}
            />
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2">
        {items.filter(item => item.count > 0).map((item, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <div className={cn('w-2 h-2 rounded-full', item.color)} />
            <span className="text-xs text-muted-foreground">
              {item.label} ({item.count})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ForgeStatsCard: React.FC<ForgeStatsCardProps> = ({ stats, className }) => {
  const { t } = useTranslation();

  // Forge status breakdown
  const forgeStatusItems = [
    { label: t('forgeDashboard.stats.deployed', 'Deployed'), count: stats.forges_by_status.deployed, color: 'bg-green-500' },
    { label: t('forgeDashboard.stats.deploying', 'Deploying'), count: stats.forges_by_status.deploying, color: 'bg-blue-500' },
    { label: t('forgeDashboard.stats.creating', 'Creating'), count: stats.forges_by_status.creating, color: 'bg-yellow-500' },
    { label: t('forgeDashboard.stats.updating', 'Updating'), count: stats.forges_by_status.updating, color: 'bg-purple-500' },
    { label: t('forgeDashboard.stats.error', 'Error'), count: stats.forges_by_status.error, color: 'bg-red-500' },
    { label: t('forgeDashboard.stats.deleting', 'Deleting'), count: stats.forges_by_status.deleting, color: 'bg-gray-500' },
  ];

  // Application status breakdown
  const appStatusItems = [
    { label: t('forgeDashboard.stats.deployed', 'Deployed'), count: stats.applications_by_status.deployed, color: 'bg-green-500' },
    { label: t('forgeDashboard.stats.building', 'Building'), count: stats.applications_by_status.building, color: 'bg-blue-500' },
    { label: t('forgeDashboard.stats.deploying', 'Deploying'), count: stats.applications_by_status.deploying, color: 'bg-cyan-500' },
    { label: t('forgeDashboard.stats.stopped', 'Stopped'), count: stats.applications_by_status.stopped, color: 'bg-gray-500' },
    { label: t('forgeDashboard.stats.error', 'Error'), count: stats.applications_by_status.error + stats.applications_by_status.build_failed, color: 'bg-red-500' },
    { label: t('forgeDashboard.stats.creating', 'Creating'), count: stats.applications_by_status.creating + stats.applications_by_status.initializing, color: 'bg-yellow-500' },
  ];

  return (
    <div className={cn('bg-card border border-border rounded-xl p-6', className)}>
      <h3 className="text-lg font-semibold text-foreground mb-6">
        {t('forgeDashboard.stats.title', 'Forge Statistics')}
      </h3>

      {/* Main stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatItem
          label={t('forgeDashboard.stats.clients', 'Clients')}
          value={stats.total_clients}
          icon={UsersRound}
        />
        <StatItem
          label={t('forgeDashboard.stats.forges', 'Forges')}
          value={stats.total_forges}
          icon={Layers}
          iconBgColor="bg-blue-100 dark:bg-blue-950/30"
          iconColor="text-blue-600 dark:text-blue-400"
        />
        <StatItem
          label={t('forgeDashboard.stats.applications', 'Applications')}
          value={stats.total_applications}
          icon={AppWindow}
          iconBgColor="bg-purple-100 dark:bg-purple-950/30"
          iconColor="text-purple-600 dark:text-purple-400"
        />
        <StatItem
          label={t('forgeDashboard.stats.pods', 'Pods')}
          value={stats.total_pods}
          icon={Layers}
          iconBgColor="bg-green-100 dark:bg-green-950/30"
          iconColor="text-green-600 dark:text-green-400"
        />
      </div>

      {/* Health summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <div>
            <p className="text-lg font-bold text-green-700 dark:text-green-400">
              {stats.healthy_applications}
            </p>
            <p className="text-xs text-green-600 dark:text-green-500">
              {t('forgeDashboard.stats.healthyApps', 'Healthy Apps')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <div>
            <p className="text-lg font-bold text-red-700 dark:text-red-400">
              {stats.unhealthy_applications}
            </p>
            <p className="text-xs text-red-600 dark:text-red-500">
              {t('forgeDashboard.stats.unhealthyApps', 'Unhealthy Apps')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <p className="text-lg font-bold text-blue-700 dark:text-blue-400">
              {stats.running_pods}/{stats.total_pods}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-500">
              {t('forgeDashboard.stats.runningPods', 'Running Pods')}
            </p>
          </div>
        </div>
      </div>

      {/* Status breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatusBreakdown
          title={t('forgeDashboard.stats.forgesByStatus', 'Forges by Status')}
          items={forgeStatusItems}
        />
        <StatusBreakdown
          title={t('forgeDashboard.stats.appsByStatus', 'Applications by Status')}
          items={appStatusItems}
        />
      </div>
    </div>
  );
};

export default ForgeStatsCard;
